import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useObs } from '../../contexts/ObsContext';
import { ProgressBar, Hint, Btn, SecLabel, BucketModal, StudentTag } from '../../components/ui';
import { BUCKETS, WHO_OPTIONS, C } from '../../theme';
import { obsAPI } from '../../api';
import { toast } from 'react-toastify';
import useRequireObs from '../../lib/useRequireObs';
import { noteFromApi } from '../../lib/obsStorage';
import useNoteGate from '../../lib/useNoteGate';
import { gateFromError } from '../../lib/noteGate';
import GateReason from '../../components/GateReason';

const STEPS = ['Context', 'Evidence', 'Summary', 'Feedback'];

const WHO_LABEL = Object.fromEntries(WHO_OPTIONS.map(o => [o.value, o.label]));

const CONFIDENCE_LABEL = { high: 'High confidence', medium: 'Medium confidence', low: 'Low confidence' };

const IMPACT_PROMPTS = [
  'What does this tell us about what students understand?',
  'What learning is this evidence of — and how deep does it go?',
  'If this continues, what will students be able to do?',
];

/* ── Timer bar component ─────────────────────────────────────────────────── */
function TimerBar({ seconds, label }) {
  const pct    = Math.min((seconds / 900) * 100, 100);
  const color  = seconds >= 900 ? '#E24B4A' : seconds >= 600 ? '#BA7517' : '#A8D8EF';
  const m = String(Math.floor(seconds / 60)).padStart(2, '0');
  const s = String(seconds % 60).padStart(2, '0');
  const cp = seconds < 600 ? '10 min checkpoint ahead' : seconds < 900 ? '15 min limit approaching' : '15 min reached';
  return (
    <div className="timer-wrap">
      <div className="timer-bar-track">
        <div className="timer-bar-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      <div className="timer-row">
        <div className="timer-display">{m}:{s}</div>
        <div className="timer-label">{label}</div>
        <div className="timer-checkpoint">{cp}</div>
      </div>
    </div>
  );
}

/* ── Timer alert overlay ─────────────────────────────────────────────────── */
function TimerAlert({ type, noteCount, onDismiss }) {
  const is15 = type === 15;
  return (
    <div className="timer-alert show">
      <div className="timer-alert-card">
        <div className="ta-icon" style={{ background: is15 ? 'var(--red-l)' : 'var(--amber-l)' }}>
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            <circle cx="11" cy="11" r="8" stroke={is15 ? 'var(--red)' : 'var(--amber)'} strokeWidth="1.8"/>
            {is15
              ? <path d="M11 7v5.5M11 15v.5" stroke="var(--red)" strokeWidth="1.6" strokeLinecap="round"/>
              : <path d="M11 7v5l3 2" stroke="var(--amber)" strokeWidth="1.6" strokeLinecap="round"/>
            }
          </svg>
        </div>
        <div className="ta-title">{is15 ? '15 minutes reached' : '10 minute checkpoint'}</div>
        <div className="ta-desc">
          {is15
            ? "You've reached the observation window. Move to review when you're ready."
            : "Every 10 minutes matters. If you haven't seen evidence of learning in this classroom — ask yourself why."
          }
        </div>
        {!is15 && noteCount < 2 && (
          <div className="ta-quality">
            <strong>Fewer than 2 evidence notes recorded.</strong>{' '}
            Before continuing, look at another part of the room. Learning is always happening somewhere.
          </div>
        )}
        <Btn variant="dark" onClick={onDismiss}>Continue observing</Btn>
      </div>
    </div>
  );
}

/* One student, a few students and about half all have to be identifiable, or
   the teacher cannot tell who the note is about. Groups are described rather
   than named — "the two at the door-side table" is what makes them
   recognisable — so for a group the toggle opens on Description. */
const NEEDS_ID = new Set(['one', 'few', 'half']);
const defaultIdType = (who) => (who === 'one' ? 'name' : 'description');
const ID_PLACEHOLDER = {
  one:  { name: 'e.g. Jake', description: 'e.g. student in the red hoodie, back left' },
  few:  { name: 'e.g. Jake and Amira', description: 'e.g. the two at the door-side table' },
  half: { name: 'e.g. Jake, Amira, Sam and Leo', description: 'e.g. the six at the two back tables' },
};
const ID_PROMPT = {
  one:  'You selected one student — please identify them so the teacher knows who you mean.',
  few:  'You selected a few students — please describe them so the teacher knows who you mean.',
  half: 'You selected about half — please describe which students so the teacher knows who you mean.',
};

/* ── Edit a saved note ────────────────────────────────────────────────────
   Any saved note can be reopened and corrected. Bucket, who, direction,
   evidence and impact are all editable. Nothing is lost. */
function EditNoteSheet({ note, bucketDefs, onClose, onSaved }) {
  const [who,      setWho]      = useState(note.who);
  const [studentId, setStudentId] = useState(note.studentId || '');
  const [idType,   setIdType]   = useState(note.idType || defaultIdType(note.who));
  const [side,     setSide]     = useState(note.side);
  const [evidence, setEvidence] = useState(note.evidence || '');
  const [impact,   setImpact]   = useState(note.impact || '');
  const [bucket,   setBucket]   = useState(note.bucket || '');
  const [exceptions, setExceptions] = useState(note.exceptions || '');
  const [saving,   setSaving]   = useState(false);
  const noteGate = useNoteGate({ evidence, impact, who, identifier: studentId });

  // Moving between groups changes what identification means, so the toggle
  // follows the group and an identifier that no longer applies is dropped.
  const changeWho = (next) => {
    if (next === who) return;
    setWho(next);
    setIdType(defaultIdType(next));
    if (!NEEDS_ID.has(next)) setStudentId('');
  };

  const save = async () => {
    // A correction is held to the same gate as a new note.
    if (!noteGate.attempt()) return;
    if (!bucket) { toast.error('Please tag this note to an impact bucket.'); return; }
    setSaving(true);
    try {
      await obsAPI.updateNote(note.id, {
        student_group:           who,
        student_identifier:      NEEDS_ID.has(who) ? studentId.trim() : '',
        student_identifier_type: NEEDS_ID.has(who) ? idType : '',
        group_exceptions:   who === 'most' ? exceptions.trim() : '',
        learning_effective: side === 'pos',
        evidence_text:      evidence,
        impact_text:        impact,
        bucket,
      });
      onSaved(note.id, { who, side, evidence, impact, bucket,
                         studentId: NEEDS_ID.has(who) ? studentId.trim() : '',
                         idType:    NEEDS_ID.has(who) ? idType : '',
                         exceptions: who === 'most' ? exceptions.trim() : '',
                         bucketColor: BUCKETS[bucket]?.dot || '' });
      toast.success('Note updated.');
      onClose();
    } catch (err) {
      const refused = gateFromError(err);
      if (refused) { noteGate.refusedByServer(refused); return; }
      console.error(err);
      toast.error('Could not update that note. Your changes have been kept — please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={e => e.stopPropagation()}>
        <div className="sheet-head">
          <div className="sheet-title">Edit note</div>
          <button className="sheet-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="sheet-body">
          <div className="sheet-lbl">How many students?</div>
          <div className="sheet-grid">
            {WHO_OPTIONS.map(o => (
              <button key={o.value}
                      className={`sheet-opt${who === o.value ? ' on' : ''}`}
                      onClick={() => changeWho(o.value)}>
                {o.label}
              </button>
            ))}
          </div>

          {NEEDS_ID.has(who) && (
            <>
              <div className="sheet-lbl">
                {who === 'one' ? 'Which student?' : 'Which students?'}
              </div>
              <div className="sb-toggle">
                <button className={`sb-opt${idType === 'name' ? ' on' : ''}`}
                        onClick={() => setIdType('name')}>First name</button>
                <button className={`sb-opt${idType === 'description' ? ' on' : ''}`}
                        onClick={() => setIdType('description')}>Description</button>
              </div>
              <input className={`fi${noteGate.errorFor('student_identifier') ? ' gate-error' : ''}`}
                     value={studentId}
                     placeholder={ID_PLACEHOLDER[who][idType]}
                     onBlur={() => noteGate.touch('student_identifier')}
                     onChange={e => setStudentId(e.target.value)} />
              <GateReason gate={noteGate.errorFor('student_identifier')} />
            </>
          )}

          {who === 'most' && (
            <>
              <div className="sheet-lbl">Which student or students?</div>
              <input className="fi" value={exceptions}
                     placeholder="e.g. two children at the door-side table"
                     onChange={e => setExceptions(e.target.value)} />
            </>
          )}

          <div className="sheet-lbl">Direction</div>
          <div className="side-toggle">
            <button className={`side-opt${side === 'pos' ? ' on pos' : ''}`} onClick={() => setSide('pos')}>
              Learning effective
            </button>
            <button className={`side-opt${side === 'neg' ? ' on neg' : ''}`} onClick={() => setSide('neg')}>
              Could be more effective
            </button>
          </div>

          <div className="sheet-lbl">What were they doing or saying?</div>
          <textarea className={`fi${noteGate.errorFor('evidence') ? ' gate-error' : ''}`}
                    rows={4} value={evidence}
                    onBlur={() => noteGate.touch('evidence')}
                    onChange={e => setEvidence(e.target.value)} />
          <GateReason gate={noteGate.errorFor('evidence')} />

          <div className="sheet-lbl">What is the impact on learning?</div>
          <textarea className={`fi${noteGate.errorFor('impact') ? ' gate-error' : ''}`}
                    rows={3} value={impact}
                    onBlur={() => noteGate.touch('impact')}
                    onChange={e => setImpact(e.target.value)} />
          <GateReason gate={noteGate.errorFor('impact')} />

          <div className="sheet-lbl">Impact bucket</div>
          <div className="bucket-list">
            {bucketDefs.map(b => {
              const colors = BUCKETS[b.key] || {};
              const on = bucket === b.key;
              return (
                <div key={b.key}
                     className={`bucket-row${on ? ' on' : ''}`}
                     style={on ? { borderColor: colors.dot, background: colors.dot + '10' } : {}}
                     onClick={() => setBucket(b.key)}>
                  <div className="bc-dot" style={{ background: colors.dot }} />
                  <div>
                    <div className="bc-name">{b.name}</div>
                    <div className="bc-one-line">{b.one_line}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="sheet-foot">
          <GateReason gate={noteGate.errorFor('who')} />
          <Btn variant="dark" onClick={save} loading={saving}
               disabled={noteGate.blocked}>Save changes</Btn>
          <button className="sheet-cancel" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function EvidencePage() {
  const navigate = useNavigate();
  const { obs, teacher, notes, addNote, updateNote, setNotesFromServer,
          seconds, startTimer, stopTimer } = useObs();
  useRequireObs();

  // The saved note currently open for correction, if any.
  const [editingNote, setEditingNote] = useState(null);

  // Form state
  const [who,       setWho]       = useState('');
  const [studentId, setStudentId] = useState('');
  const [idType,    setIdType]    = useState('name');
  const [side,      setSide]      = useState('pos');
  const [evidence,  setEvidence]  = useState('');
  const [impact,    setImpact]    = useState('');
  const [bucket,    setBucket]    = useState('');
  // "Most or all" is rarely all of them — who the note leaves out.
  const [exceptions, setExceptions] = useState('');
  const [bucketColor, setBucketColor] = useState('');

  // Nothing is saved until the note passes the gate. The server runs the same
  // check on every save; this one answers as soon as a field is left.
  const noteGate = useNoteGate({ evidence, impact, who, identifier: studentId });

  // Same rule as the correction sheet: the identification toggle follows the
  // group, and an identifier that no longer applies is dropped rather than
  // left to travel silently with the note.
  const changeWho = useCallback((next) => {
    setWho(prev => {
      if (prev === next) return prev;
      setIdType(defaultIdType(next));
      if (!NEEDS_ID.has(next)) setStudentId('');
      return next;
    });
  }, []);

  // Bucket definitions come from the API — the same definitions the
  // classifier prompt is built from, so the two cannot drift apart.
  const [bucketDefs, setBucketDefs] = useState([]);
  const [tieBreaker, setTieBreaker] = useState('');
  const [exceptionPresets, setExceptionPresets] = useState([]);

  // Suggestion state. Nothing is ever pre-selected: `bucket` stays empty
  // until the observer taps. If the app selected one for them, every save
  // would silently become a confirmation.
  const [suggestion,   setSuggestion]   = useState(null);
  const [suggesting,   setSuggesting]   = useState(false);
  const [showAll,      setShowAll]      = useState(false);
  const [decisionPath, setDecisionPath] = useState('');

  // Who else is observing this lesson. Counts only — the notes stay private
  // until the observation closes.
  const [participants, setParticipants] = useState(null);

  // Timer alerts
  const [alert10,   setAlert10]   = useState(false);
  const [alert15,   setAlert15]   = useState(false);
  const [alertType, setAlertType] = useState(null);

  // Bucket info modal
  const [bucketModal, setBucketModal] = useState(null);

  // Skill prompt
  const [skillPrompt, setSkillPrompt] = useState('');

  // Start timer on mount
  useEffect(() => {
    startTimer();
    return () => {}; // don't stop on unmount — keep running across re-renders
  }, []);

  // Check timer alerts
  useEffect(() => {
    if (seconds === 600 && !alert10) { setAlert10(true); setAlertType(10); }
    if (seconds === 900 && !alert15) { setAlert15(true); setAlertType(15); }
  }, [seconds]);

  // Recover the notes already saved for this observation. Storage covers the
  // usual refresh; this covers the case where it is unavailable or stale.
  useEffect(() => {
    if (!obs?.id || notes.length > 0) return;
    let cancelled = false;
    obsAPI.notes(obs.id)
      .then(({ data }) => {
        const list = data.results ?? data;
        if (!cancelled && list.length) {
          setNotesFromServer(list.map(noteFromApi).reverse());
        }
      })
      .catch(err => console.error('Could not load saved notes:', err));
    return () => { cancelled = true; };
    // Runs on arrival only — later note changes must not retrigger it.
  }, [obs?.id]);   // eslint-disable-line

  // Poll for co-observers joining. Cheap, and the lead wants to know someone
  // arrived without refreshing.
  useEffect(() => {
    if (!obs?.id || !obs?.is_co_observation) return;
    let cancelled = false;
    const fetch = () => obsAPI.participants(obs.id)
      .then(({ data }) => { if (!cancelled) setParticipants(data); })
      .catch(() => {});
    fetch();
    const t = setInterval(fetch, 20000);
    return () => { cancelled = true; clearInterval(t); };
  }, [obs?.id, obs?.is_co_observation]);

  // Canonical bucket definitions.
  useEffect(() => {
    obsAPI.buckets()
      .then(({ data }) => {
        setBucketDefs(data.buckets);
        setTieBreaker(data.tie_breaker);
        setExceptionPresets(data.exception_presets || []);
      })
      .catch(err => console.error('Could not load bucket definitions:', err));
  }, []);

  // As the observer types, once the note is substantive, ask for a suggestion.
  // Debounced so it follows the typing rather than fighting it.
  useEffect(() => {
    if (bucket) return;               // They have already decided.
    const t = setTimeout(async () => {
      if (evidence.trim().split(/\s+/).length < 5 || impact.trim().split(/\s+/).length < 4) {
        setSuggestion(null);
        return;
      }
      setSuggesting(true);
      try {
        const { data } = await obsAPI.suggestBucket({
          evidence_text: evidence, impact_text: impact,
        });
        setSuggestion(data.suggestion);
      } catch {
        setSuggestion(null);          // Silent — a missing suggestion is valid.
      } finally {
        setSuggesting(false);
      }
    }, 700);
    return () => clearTimeout(t);
  }, [evidence, impact, bucket]);

  // Skill prompt logic
  useEffect(() => {
    if (notes.length < 2) { setSkillPrompt(''); return; }
    const counts = {};
    notes.forEach(n => { counts[n.bucket] = (counts[n.bucket] || 0) + 1; });
    const keys = Object.keys(counts);
    if (keys.length === 1 && counts[keys[0]] >= 2) {
      setSkillPrompt(`You have ${counts[keys[0]]} notes tagged to ${BUCKETS[keys[0]]?.name}. Have you looked at other parts of the room?`);
    } else {
      setSkillPrompt('');
    }
  }, [notes]);

  const bucketDef     = key => bucketDefs.find(b => b.key === key);
  const bucketName    = key => bucketDef(key)?.name || BUCKETS[key]?.name || key;
  const bucketOneLine = key => bucketDef(key)?.one_line || '';

  /** Every route to a bucket goes through here, so the decision path is
   *  always recorded alongside the choice. */
  const chooseBucket = (key, path) => {
    setBucket(key);
    setBucketColor(BUCKETS[key]?.dot || '');
    setDecisionPath(path);
  };

  // What will be written when this note is saved. The overrides are the
  // valuable rows — they mark where the framework itself is ambiguous.
  const captureRow = {
    suggested_bucket:      suggestion?.bucket || '',
    suggested_alternative: suggestion?.alternative || '',
    suggestion_confidence: suggestion?.confidence || '',
    suggestion_rationale:  suggestion?.rationale || '',
    suggestion_source:     suggestion?.source || 'none',
    decision_path:         decisionPath || (suggestion ? '' : 'no_suggestion'),
    chosen_bucket:         bucket,
    was_override:          !!(suggestion?.bucket && bucket && bucket !== suggestion.bucket),
  };

  const saveNote = async () => {
    if (!noteGate.attempt()) return;
    if (!bucket) { toast.error('Please tag this note to an impact bucket.'); return; }
    const note = {
      who, studentId: studentId.trim(), idType,
      exceptions: who === 'most' ? exceptions.trim() : '',
      side, evidence, impact, bucket, bucketColor,
    };
    

    try {
      const { data: saved } = await obsAPI.addNote({
        observation: obs?.id,
        student_group: who,
        student_identifier: NEEDS_ID.has(who) ? studentId.trim() : '',
        student_identifier_type: NEEDS_ID.has(who) ? idType : '',
        group_exceptions: who === 'most' ? exceptions.trim() : '',
        learning_effective: side === 'pos',
        evidence_text: evidence,
        impact_text: impact,
        bucket,
        suggested_bucket:      captureRow.suggested_bucket,
        suggested_alternative: captureRow.suggested_alternative,
        suggestion_confidence: captureRow.suggestion_confidence,
        suggestion_rationale:  captureRow.suggestion_rationale,
        decision_path:         captureRow.decision_path,
        timestamp_seconds: seconds,
      });
      addNote({ ...note, id: saved.id });
      // Only clear the form once the note is safely saved — otherwise the
      // observer loses what they typed with no way to get it back mid-lesson.
      setWho(''); setStudentId(''); setIdType('name'); setSide('pos');
      setExceptions('');
      setEvidence(''); setImpact(''); setBucket(''); setBucketColor('');
      setSuggestion(null); setDecisionPath(''); setShowAll(false);
      noteGate.reset();
    } catch (error) {
      // Refused by the server's gate: say why beside the field, and keep
      // everything the observer typed.
      const refused = gateFromError(error);
      if (refused) { noteGate.refusedByServer(refused); return; }
      console.error(error);
      toast.error('Could not save that note. Your text has been kept — please try again.');
    }
  };

  const handleReview = async () => {
    if (notes.length === 0) {
      toast.error('Record at least one evidence note before reviewing.');
      return;
    }
    // With more than one observer the notes have to be brought together
    // before anyone reviews them.
    const shared = (participants?.observers?.length || 1) > 1;
    if (shared) {
      try {
        await obsAPI.complete(obs.id);
        navigate('/observe/reconcile');
        return;
      } catch (err) {
        console.error(err);
        toast.error('Could not close the observation. Please try again.');
        return;
      }
    }
    navigate('/observe/conclusions');
  };

  const timerLabel = teacher
    ? `${teacher.full_name} · ${obs?.grade_level} · ${obs?.subject}`
    : `${obs?.grade_level || ''} · ${obs?.subject || ''}`;

  return (
    <div className="screen" style={{ position: 'relative' }}>
      {/* Timer alert */}
      {alertType && (
        <TimerAlert type={alertType} noteCount={notes.length} onDismiss={() => setAlertType(null)} />
      )}

      {/* Bucket modal */}
      {bucketModal && <BucketModal bucketKey={bucketModal} onClose={() => setBucketModal(null)} />}

      {/* Correcting a saved note */}
      {editingNote && (
        <EditNoteSheet
          note={editingNote}
          bucketDefs={bucketDefs}
          onClose={() => setEditingNote(null)}
          onSaved={updateNote}
        />
      )}

      <div className="obsbar">
        <button className="obsbar-back" onClick={() => {
          if (notes.length > 0 && !window.confirm('Leave this observation? Your notes will be lost.')) return;
          stopTimer(); navigate('/');
        }}>← Back</button>
        <div className="obsbar-title">Evidence</div>
      </div>

      <TimerBar seconds={seconds} label={timerLabel} />


      <ProgressBar steps={STEPS} current={1} />

      <div className="scroll">
        <div className="pad">
          {/* Saved notes — any of them can be reopened and corrected. */}
          {notes.length > 0 && (
            <div className="ev-saved-lbl">Saved notes · {notes.length}</div>
          )}
          {notes.map(n => {
            const b = BUCKETS[n.bucket];
            const neg = n.side === 'neg';
            return (
              <div key={n.id} className="ev-note">
                <div className="ev-meta">
                  <span className="ev-who">
                    {WHO_LABEL[n.who]}
                    {n.exceptions ? ` · ${n.exceptions}` : ''}
                  </span>
                  {n.studentId && <StudentTag name={n.studentId} />}
                  <span className={`ev-side ${neg ? 'neg' : 'pos'}`}>
                    {neg ? 'Could be more effective' : 'Learning effective'}
                  </span>
                  <button className="ev-edit" onClick={() => setEditingNote(n)}>Edit ›</button>
                </div>
                <div className="ev-text">{n.evidence}</div>
                <div className="ev-impact">{n.impact}</div>
                {b && (
                  <span className="ev-bucket" style={{ background: b.dot+'1A', color: b.dot }}>
                    {b.name}
                  </span>
                )}
              </div>
            );
          })}

          {/* Skill prompt */}
          {skillPrompt && (
            <div className="skill-prompt hint-amber">
              <span>⚠</span>
              <p>{skillPrompt}</p>
            </div>
          )}

          {/* Note form */}
          <div className="card card-pad">
            <SecLabel style={{ marginTop: 0 }}>Part 1 — how many students?</SecLabel>
            <div className="chip-row">
              {WHO_OPTIONS.map(opt => (
                <button key={opt.value} className={`chip${who === opt.value ? ' on' : ''}`} onClick={() => changeWho(opt.value)}>
                  {opt.label}
                </button>
              ))}
            </div>
            <GateReason gate={noteGate.errorFor('who')} />

            {/* One student, a few or about half. All have to be identifiable. */}
            {NEEDS_ID.has(who) && (
              <div style={{ marginTop: 10 }}>
                <div className="required-flag">
                  <span style={{ color:'var(--red)', fontSize:13, flexShrink:0 }}>!</span>
                  <div style={{ fontSize:11, color:'var(--red-d)', lineHeight:1.45 }}>
                    {ID_PROMPT[who]}
                  </div>
                </div>
                <div className="student-block">
                  <div className="sb-label">
                    👤 {who === 'one' ? 'Which student?' : 'Which students?'}
                  </div>
                  <div className="sb-toggle">
                    <button className={`sb-opt${idType === 'name' ? ' on' : ''}`} onClick={() => setIdType('name')}>First name</button>
                    <button className={`sb-opt${idType === 'description' ? ' on' : ''}`} onClick={() => setIdType('description')}>Description</button>
                  </div>
                  <input
                    className={`sb-input${noteGate.errorFor('student_identifier') ? ' gate-error' : ''}`}
                    value={studentId}
                    onBlur={() => noteGate.touch('student_identifier')}
                    onChange={e => setStudentId(e.target.value)}
                    placeholder={ID_PLACEHOLDER[who][idType]}
                  />
                  <GateReason gate={noteGate.errorFor('student_identifier')} />
                  <div className="sb-hint">
                    {idType === 'name'
                      ? 'Only you and the teacher will see this. Not included in dashboards.'
                      : 'Be specific enough that the teacher will know exactly who you mean.'}
                  </div>
                </div>
              </div>
            )}

            {/* Most is rarely all. Saying who it leaves out is the difference
                between the teacher recognising the students and guessing. */}
            {who === 'most' && (
              <div className="student-block" style={{ marginTop: 10 }}>
                <div className="sb-label">👤 Which student or students?</div>
                <input
                  className="sb-input"
                  value={exceptions}
                  onChange={e => setExceptions(e.target.value)}
                  placeholder="e.g. two children at the door-side table"
                />

                {exceptionPresets.length > 0 && (
                  <>
                    <div className="exc-lbl">Common exceptions</div>
                    <div className="exc-chips">
                      {exceptionPresets.map(preset => (
                        <button key={preset} type="button"
                                className={`exc-chip${exceptions === preset ? ' on' : ''}`}
                                onClick={() => setExceptions(exceptions === preset ? '' : preset)}>
                          {preset}
                        </button>
                      ))}
                    </div>
                  </>
                )}

                <div className="sb-hint">
                  Be specific enough that the teacher will know exactly who you mean.
                </div>
              </div>
            )}

            <SecLabel>Part 2 — what were they doing or saying?</SecLabel>
            <div className="side-toggle">
              <button className={`side-opt${side === 'pos' ? ' on pos' : ''}`} onClick={() => setSide('pos')}>
                Learning effective
              </button>
              <button className={`side-opt${side === 'neg' ? ' on neg' : ''}`} onClick={() => setSide('neg')}>
                Could be more effective
              </button>
            </div>
            <div style={{ fontSize:12, fontWeight:500, marginBottom:5, color: side === 'pos' ? 'var(--teal)' : 'var(--purple)' }}>
              {side === 'pos' ? 'Learning was most effective for…' : 'Learning could have been more effective for…'}
            </div>
            <textarea
              className={`fi${noteGate.errorFor('evidence') ? ' gate-error' : ''}`}
              rows={3}
              value={evidence}
              onBlur={() => noteGate.touch('evidence')}
              onChange={e => setEvidence(e.target.value)}
              placeholder="What did you see or hear? Quote the words where you can."
              style={{ marginTop: 5 }}
            />
            <GateReason gate={noteGate.errorFor('evidence')} />

            <SecLabel style={{ marginTop: 12 }}>Part 3 — what is the impact on learning?</SecLabel>
            <textarea
              className={`fi${noteGate.errorFor('impact') ? ' gate-error' : ''}`}
              rows={2}
              value={impact}
              onBlur={() => noteGate.touch('impact')}
              onChange={e => setImpact(e.target.value)}
              placeholder="What does this tell us about their learning?"
            />
            <GateReason gate={noteGate.errorFor('impact')} />
            {/* Guidance for the observer to think with — not content to insert,
                so these are plain text with no click behaviour. */}
            <div className="guidance">
              <div className="guidance-lbl">Guidance</div>
              <ul>
                {IMPACT_PROMPTS.map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            </div>

            <SecLabel style={{ marginTop: 12 }}>
              Tag to an impact bucket
              {suggestion && !showAll && <span className="sec-chip">Suggested</span>}
            </SecLabel>

            {/* Before there is anything to read, the list stays out of the
                way — but is always one tap away. */}
            {!suggestion && !showAll && (
              <>
                <div className="sugg-placeholder">
                  {suggesting
                    ? 'Reading your note…'
                    : 'Write the evidence and the impact and the app will suggest a bucket. You can pick your own at any time.'}
                </div>
                <button className="sugg-more" onClick={() => setShowAll(true)}>
                  Choose from all seven buckets
                </button>
              </>
            )}

            {suggestion && !showAll && (
              <>
                <div className="sugg">
                  <div className="sugg-head">
                    <span className="sugg-lbl">Suggested bucket</span>
                    <span className="sugg-chips">
                      {suggestion.source !== 'model' && <span className="chip-offline">Offline</span>}
                      <span className={`sugg-conf sugg-conf-${suggestion.confidence}`}>
                        {CONFIDENCE_LABEL[suggestion.confidence] || suggestion.confidence}
                      </span>
                    </span>
                  </div>

                  {/* One tap to confirm. */}
                  <button
                    className={`sugg-opt${bucket === suggestion.bucket ? ' on' : ''}`}
                    onClick={() => chooseBucket(suggestion.bucket, 'confirmed')}
                  >
                    <span className="bc-dot" style={{ background: BUCKETS[suggestion.bucket]?.dot }} />
                    <span className="sugg-opt-body">
                      <span className="sugg-opt-name">{bucketName(suggestion.bucket)}</span>
                      <span className="sugg-opt-def">{bucketOneLine(suggestion.bucket)}</span>
                    </span>
                    <span className="sugg-radio" />
                  </button>

                  <div className="sugg-why">{suggestion.rationale}</div>

                  {/* One tap to take the alternative instead. */}
                  <div className="sugg-alt-row">
                    <span className="sugg-or">Or</span>
                    <button
                      className={`sugg-alt${bucket === suggestion.alternative ? ' on' : ''}`}
                      onClick={() => chooseBucket(suggestion.alternative, 'alternative')}
                    >
                      {bucketName(suggestion.alternative)}
                    </button>
                  </div>
                </div>

                {suggestion.source !== 'model' && (
                  <div className="sugg-offline-note">
                    <strong>No model connection.</strong> The suggestion above came from a
                    simple keyword match, not the classifier. It is a placeholder so the
                    interaction can be demonstrated. Connected, the model reads the whole
                    note and applies the tie-breaker.
                  </div>
                )}

                {/* One tap to open the full list. */}
                <button className="sugg-more" onClick={() => setShowAll(true)}>
                  Choose a different bucket
                </button>
              </>
            )}

            {/* The full list — only once the observer has asked for it. */}
            {showAll && (
              <>
                <div className="bucket-list">
                  {bucketDefs.map(b => {
                    const colors = BUCKETS[b.key] || {};
                    const on = bucket === b.key;
                    return (
                      <div
                        key={b.key}
                        className={`bucket-row${on ? ' on' : ''}`}
                        style={on ? { borderColor: colors.dot, background: colors.dot + '10' } : {}}
                        onClick={() => chooseBucket(b.key, suggestion ? 'manual' : 'no_suggestion')}
                      >
                        <div className="bc-dot" style={{ background: colors.dot }} />
                        <div>
                          <div className="bc-name">{b.name}</div>
                          <div className="bc-one-line">{b.one_line}</div>
                          {/* The longer line is revealed on selection only. */}
                          {on && <div className="bc-choose-when">{b.choose_when}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {tieBreaker && <div className="tie-breaker">{tieBreaker}</div>}
              </>
            )}

            <Btn variant="dark" onClick={saveNote} disabled={noteGate.blocked}>
              Save note &amp; add another
            </Btn>
          </div>

          {/* Session code and who has joined. Observing alone has a code too,
              but there is nobody to share it with. */}
          {obs?.is_co_observation && obs?.session_code && (
            <div className="co-live">
              <div className="co-live-row">
                <span className="co-lbl" style={{ margin: 0 }}>Session code</span>
                <span className="co-live-code">{obs.session_code}</span>
              </div>
              <div className="co-hint" style={{ marginTop: 6 }}>
                {participants?.is_lead
                  ? 'Share this with anyone joining you. You are the lead, so you run the debrief.'
                  : 'You joined this observation. The lead runs the debrief.'}
              </div>

              {participants?.observers?.length > 1 && (
                <div className="co-live-people">
                  {participants.observers.map(o => (
                    <span key={o.id} className="co-person">
                      <span className="co-av">{o.initials}</span>
                      {o.is_you ? 'You' : o.name}
                      {o.is_lead && <span className="co-lead-tag">lead</span>}
                      <span className="co-note-count">{o.note_count}</span>
                    </span>
                  ))}
                </div>
              )}

              {participants?.observers?.length > 1 && (
                <div className="co-hint">
                  You can see how many notes each observer has, not what they say —
                  the notes open up once the observation is closed.
                </div>
              )}
            </div>
          )}

          {/* There is nothing to review until at least one note exists. */}
          <Btn variant="blue" onClick={handleReview}>
            Summary of findings →
          </Btn>

        </div>
      </div>
    </div>
  );
}
